import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState, useRef } from 'react';
import { compact, debounce, isEqual } from 'lodash-es';
import { InvoiceUpdatedType, PusherEventName } from 'anchor-scout-common/types/pusher';
import { PageContentHeader, TableListProvider, TableListHeader, useMediaQueries, Spinner, PaginationControl, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { AccountingStatusFilter } from '~/business-logic/qboStatusFilterLogic';
import TableFilters from '~/components/TableFilters/TableFilters';
import { ErrorText } from '~/constants';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useUnmarkPaidExternally } from '~/hooks/invoice/useUnmarkPaidExternally';
import { useUnvoidInvoice } from '~/hooks/invoice/useUnvoidInvoice';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { useRestfulWrapper } from '~/hooks/useRestfulWrapper';
import { useSortFromUrl } from '~/hooks/useSortFromUrl';
import { useLocaleKeys } from '~/i18n';
import { handlePromise } from '~/infra/utils';
import { invoiceModel } from '~/models';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import { useSubscribeToInvoiceChangesObservable } from '~/providers/InvoiceChangesObservableProvider';
import { BusinessChannelEventBinder } from '~/pusher/BusinessChannelEventBinder';
import { selectors } from '~/store/state-selectors';
import { useAppConfig } from '~/store/store-context';
import { getURLWithFullPageInvoice } from '../FullPageInvoice/FullPageInvoice.utils';
import { NoFilteredInvoices } from '../InvoicesEmptyStates/InvoicesEmptyStates';
import { DateFilterPickerPresets } from './DateFilterPicker';
import styles from './InvoicesTable.module.scss';
import { applyOptimisticUpdate, getPaymentStatusFilterOptions, getInvoiceStatusFilterOptions, getAccountingStatusFilterOptions, mapSortAndFiltersRequestParams, getTableDefs } from './InvoicesTable.utils';
import { FilterType as InvoiceFilterType, getTableFilterLabels, formatFilterDescription, getActiveFilterGroups, getTableFilterConfig, invoiceFilterTextsPath } from './invoicesTableFilters';
import InvoicesTableRow from './InvoicesTableRow';
const defaultFilters = {
    date: DateFilterPickerPresets.AllTime,
    status: [],
    paymentStatus: [],
    search: '',
    qbo: AccountingStatusFilter.All,
    xero: AccountingStatusFilter.All
};
export function InvoicesTableWithData(props) {
    const { filtersClassname, filters, columnDefs, headerItems, sortOptions, hideDueDate, sort, loading, pageNumber, isEmptyFilters, onSortChange, onFilterChange, onResetFilters, retry, onPageNumberChange } = props;
    const { statusFilterCounters, paymentStatusFilterCounters, qboStatusFilterCounters, xeroStatusFilterCounters, total } = props.data;
    const { search, status: statusFilter, paymentStatus: paymentStatusFilter, date: dateFilter, qbo: qboStatusFilter, xero: xeroStatusFilter } = filters;
    const { paginationPageSize } = useAppConfig();
    const { XERO_INTEGRATION, INVOICES_DASHBOARD_MOBILE } = useFeatureFlags();
    const { t: invoiceFilterTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const [invoicesFromServer, setInvoicesFromServer] = useState(props.data.invoices);
    useEffect(()=>setInvoicesFromServer(props.data.invoices), [
        props.data.invoices
    ]);
    const { getNow } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const { logBI, fetchIntegrationsSummary } = useStoreActions();
    const { startUnvoidInvoice, finishUnvoidInvoice } = useUnvoidInvoice();
    const { startUnmarkPaidExternally, finishUnmarkPaidExternally } = useUnmarkPaidExternally();
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11451);
    const invoiceStatusFilterOptions = getInvoiceStatusFilterOptions({
        counters: statusFilterCounters,
        texts: invoiceFilterTexts.invoiceDisplayStatusLabels
    });
    const paymentStatusFilterOptions = getPaymentStatusFilterOptions({
        isVendor,
        counters: paymentStatusFilterCounters,
        texts: invoiceFilterTexts.paymentDisplayStatusLabels
    });
    const qboStatusFilterOptions = getAccountingStatusFilterOptions({
        counters: qboStatusFilterCounters,
        texts: invoiceFilterTexts.accountingStatusLabels
    });
    const xeroStatusFilterOptions = getAccountingStatusFilterOptions({
        counters: xeroStatusFilterCounters,
        texts: invoiceFilterTexts.accountingStatusLabels
    });
    const tableFilterConfig = getTableFilterConfig({
        invoiceStatusFilterOptions,
        paymentStatusFilterOptions,
        qboStatusFilterOptions,
        xeroStatusFilterOptions
    });
    const tableSelectedFilters = useMemo(()=>{
        const next = {};
        if (statusFilter.length > 0) next[InvoiceFilterType.InvoiceStatus] = statusFilter;
        if (paymentStatusFilter.length > 0) next[InvoiceFilterType.PaymentStatus] = paymentStatusFilter;
        if (dateFilter !== DateFilterPickerPresets.AllTime) next[InvoiceFilterType.Date] = dateFilter;
        if (qboStatusFilter !== AccountingStatusFilter.All) next[InvoiceFilterType.QboSync] = qboStatusFilter;
        if (xeroStatusFilter !== AccountingStatusFilter.All) next[InvoiceFilterType.XeroSync] = xeroStatusFilter;
        return next;
    }, [
        statusFilter,
        paymentStatusFilter,
        dateFilter,
        qboStatusFilter,
        xeroStatusFilter
    ]);
    const activeFilterGroups = getActiveFilterGroups({
        showQboIntegration,
        showXeroIntegration,
        xeroIntegrationEnabled: !!XERO_INTEGRATION,
        hasQboFilterOptions: qboStatusFilterOptions.length > 0,
        hasXeroFilterOptions: xeroStatusFilterOptions.length > 0,
        filterGroupLabels: invoiceFilterTexts.filterGroupLabels
    });
    const handleTableFiltersRefresh = (newFilters, newSort)=>{
        var _newFilters_InvoiceFilterType_InvoiceStatus, _newFilters_InvoiceFilterType_PaymentStatus, _newFilters_InvoiceFilterType_Date, _newFilters_InvoiceFilterType_QboSync, _newFilters_InvoiceFilterType_XeroSync;
        onFilterChange({
            status: (_newFilters_InvoiceFilterType_InvoiceStatus = newFilters[InvoiceFilterType.InvoiceStatus]) !== null && _newFilters_InvoiceFilterType_InvoiceStatus !== void 0 ? _newFilters_InvoiceFilterType_InvoiceStatus : [],
            paymentStatus: (_newFilters_InvoiceFilterType_PaymentStatus = newFilters[InvoiceFilterType.PaymentStatus]) !== null && _newFilters_InvoiceFilterType_PaymentStatus !== void 0 ? _newFilters_InvoiceFilterType_PaymentStatus : [],
            date: (_newFilters_InvoiceFilterType_Date = newFilters[InvoiceFilterType.Date]) !== null && _newFilters_InvoiceFilterType_Date !== void 0 ? _newFilters_InvoiceFilterType_Date : DateFilterPickerPresets.AllTime,
            qbo: (_newFilters_InvoiceFilterType_QboSync = newFilters[InvoiceFilterType.QboSync]) !== null && _newFilters_InvoiceFilterType_QboSync !== void 0 ? _newFilters_InvoiceFilterType_QboSync : AccountingStatusFilter.All,
            xero: (_newFilters_InvoiceFilterType_XeroSync = newFilters[InvoiceFilterType.XeroSync]) !== null && _newFilters_InvoiceFilterType_XeroSync !== void 0 ? _newFilters_InvoiceFilterType_XeroSync : AccountingStatusFilter.All
        });
        onSortChange(newSort);
    };
    useSubscribeToBillingObservable(()=>retry());
    useSubscribeToInvoiceChangesObservable(async (param)=>{
        let { optimisticUpdate } = param;
        if (optimisticUpdate) {
            setInvoicesFromServer((inv)=>applyOptimisticUpdate(inv, optimisticUpdate, getNow()));
            retry({
                disableFetchInvoices: true
            });
        } else {
            retry();
        }
    });
    useOnMount(()=>{
        if (showQboIntegration) {
            fetchIntegrationsSummary();
        }
    });
    const handleSortChange = (sort)=>{
        logBI(11450, {
            column: sort ? headerItems[sort.colIndex].id : 'none'
        });
        onSortChange(sort);
    };
    function applyInvoiceFromPusherEvent(invoice) {
        setInvoicesFromServer((invoices)=>{
            return invoices.map((i)=>{
                if (i.id === invoice.id) {
                    return invoiceModel.fromDTO(isVendor, invoice);
                }
                return i;
            });
        });
    }
    return /*#__PURE__*/ _jsxs(TableListProvider, {
        hasActionColumn: true,
        columnDefs: columnDefs,
        sort: sort,
        onSortChange: handleSortChange,
        children: [
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "scout",
                eventName: PusherEventName.InvoiceUpdated,
                callback: (payload)=>{
                    if (payload.type === InvoiceUpdatedType.RefundFailed) {
                        applyInvoiceFromPusherEvent(payload.invoice);
                    } else if (payload.type === InvoiceUpdatedType.Unvoided) {
                        applyInvoiceFromPusherEvent(payload.invoice);
                        finishUnvoidInvoice(payload.invoice.id);
                    } else if (payload.type === InvoiceUpdatedType.UnmarkedAsPaidExternally) {
                        applyInvoiceFromPusherEvent(payload.invoice);
                        finishUnmarkPaidExternally(payload.invoice.id);
                    }
                }
            }),
            /*#__PURE__*/ _jsxs(PageContentHeader, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: filtersClassname,
                        children: /*#__PURE__*/ _jsx(TableFilters, {
                            disableFlexWrap: true,
                            testIdPrefix: "invoices-dashboard",
                            filterBarTestId: "invoices-dashboard-filter-picker-group",
                            search: {
                                value: search,
                                testId: 'invoices-dashboard-search',
                                registerHotkey: true,
                                onChange: (event)=>{
                                    const search = event.target.value;
                                    onFilterChange({
                                        search
                                    }, {
                                        withDebounce: true
                                    });
                                    debouncedLogSearchToBI.onChange(search);
                                },
                                onClear: ()=>{
                                    onFilterChange({
                                        search: ''
                                    });
                                    debouncedLogSearchToBI.onClear();
                                }
                            },
                            filterLabels: getTableFilterLabels(invoiceFilterTexts.filterLabels),
                            filterConfig: tableFilterConfig,
                            filterGroups: activeFilterGroups,
                            alwaysShowAllFilters: true,
                            selectedFilters: tableSelectedFilters,
                            setSelectedFilters: ()=>{},
                            onRefresh: handleTableFiltersRefresh,
                            onClear: onResetFilters,
                            isClearDisabled: isEmptyFilters,
                            mobile: {
                                formatFilterDescription: (filter, value)=>formatFilterDescription({
                                        filterType: filter,
                                        value,
                                        texts: invoiceFilterTexts
                                    }),
                                sortOptions
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(TableListHeader, {
                        testId: "invoices-table-header",
                        items: headerItems
                    })
                ]
            }),
            loading ? /*#__PURE__*/ _jsx(Spinner, {
                centeredInBox: true,
                centerBoxHeight: 200
            }) : invoicesFromServer.length > 0 ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    invoicesFromServer.map((invoice, index)=>/*#__PURE__*/ _jsx(InvoicesTableRow, {
                            invoice: invoice,
                            index: index,
                            hideDueDate: hideDueDate,
                            showQboIntegration: showQboIntegration,
                            showXeroIntegration: showXeroIntegration,
                            to: getURLWithFullPageInvoice({
                                invoiceId: invoice.id,
                                relationshipId: invoiceModel.relationshipId(invoice)
                            }),
                            retry: retry,
                            onUnvoidInvoice: ()=>startUnvoidInvoice(invoice),
                            onUnmarkPaidExternally: ()=>startUnmarkPaidExternally(invoice)
                        }, invoice.id)),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.paginationControls,
                        children: /*#__PURE__*/ _jsx(PaginationControl, {
                            compact: INVOICES_DASHBOARD_MOBILE,
                            pageSize: paginationPageSize,
                            pageNumber: pageNumber,
                            total: total,
                            onPageNumberChange: onPageNumberChange
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsx(NoFilteredInvoices, {
                resetFilters: onResetFilters
            })
        ]
    });
}
export default function InvoicesTable(props) {
    const { relationshipId, filtersClassname, onRetry, renderLoader, renderError, renderChild, renderNoInvoices } = props;
    const { getNow, downloadCsv } = useIoContext();
    const { paginationPageSize, inputDebounceTime, exportCsvFilename } = useAppConfig();
    const [pageNumber, _setPageNumber] = useState(1);
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { smallScreen } = useMediaQueries();
    const hideDueDate = smallScreen;
    const { api } = useStoreActions();
    const { showErrorToast } = useOverlayContext();
    const { t: invoiceTexts } = useLocaleKeys(invoiceFilterTextsPath);
    const [exportingCsv, setExportingCsv] = useState(false);
    const defaultSort = useMemo(()=>({
            colIndex: 0,
            sort: 'desc'
        }), []);
    const { columnDefs, headerItems, sortIds, sortOptions } = useMemo(()=>getTableDefs({
            showQboIntegration,
            hideDueDate,
            showXeroIntegration,
            texts: invoiceTexts.columnHeaders
        }), [
        showQboIntegration,
        hideDueDate,
        showXeroIntegration
    ]);
    const { sort, changeSort: _changeSort } = useSortFromUrl({
        defaultSort,
        sortIds
    });
    const { urlState, changeUrlState: _changeUrlState } = useStateFromUrl({
        status: {
            multi: true,
            defaultValue: defaultFilters.status
        },
        paymentStatus: {
            multi: true,
            defaultValue: defaultFilters.paymentStatus
        },
        date: {
            defaultValue: defaultFilters.date
        },
        search: {
            defaultValue: defaultFilters.search
        },
        qbo: {
            defaultValue: defaultFilters.qbo
        },
        xero: {
            defaultValue: defaultFilters.xero
        }
    });
    const urlStateRef = useRef(urlState);
    const sortRef = useRef(sort);
    const pageNumberRef = useRef(pageNumber);
    const { renderRestfulState, refresh } = useRestfulWrapper({
        fetch: ()=>api.invoice.getAll(_object_spread_props(_object_spread({}, mapSortAndFiltersRequestParams(_object_spread_props(_object_spread({}, urlStateRef.current), {
                sort: sortRef.current,
                now: getNow(),
                sortIds
            }))), {
                relationshipId,
                pageSize: paginationPageSize,
                pageNumber: pageNumberRef.current
            }))
    });
    useOnMount(()=>{
        return ()=>refreshWithDebounce.cancel();
    });
    const refreshWithDebounce = useMemo(()=>{
        const debounceTime = inputDebounceTime != null ? inputDebounceTime : 500;
        return debounce(refresh, debounceTime);
    }, []);
    function changeUrlState(newUrlState) {
        _changeUrlState(newUrlState);
        urlStateRef.current = _object_spread({}, urlStateRef.current, newUrlState);
    }
    function changeSort(newSort) {
        _changeSort(newSort);
        sortRef.current = newSort;
    }
    function setPageNumber(pageNumber) {
        _setPageNumber(pageNumber);
        pageNumberRef.current = pageNumber;
    }
    function handleFilterChange(newUrlState, opts) {
        changeUrlState(newUrlState);
        setPageNumber(1);
        scheduleRefresh(opts);
    }
    function handleSortChange(sort) {
        changeSort(sort);
        setPageNumber(1);
        scheduleRefresh();
    }
    function handlePageNumberChange(pageNumber) {
        setPageNumber(pageNumber);
        scheduleRefresh();
    }
    function scheduleRefresh() {
        let { withDebounce } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        refreshWithDebounce();
        !withDebounce && refreshWithDebounce.flush();
    }
    return renderRestfulState({
        renderLoader,
        renderError,
        renderChild: (param)=>{
            let { data, loading, refresh } = param;
            const isEmptyFilters = isEqual(urlState, defaultFilters);
            if (!data.invoices.length && !loading && renderNoInvoices && isEmptyFilters) {
                return renderNoInvoices();
            }
            const tableContent = /*#__PURE__*/ _jsx(InvoicesTableWithData, {
                data: data,
                columnDefs: columnDefs,
                headerItems: headerItems,
                sortOptions: sortOptions,
                hideDueDate: hideDueDate,
                sort: sort,
                filtersClassname: filtersClassname,
                filters: urlState,
                isEmptyFilters: isEmptyFilters,
                loading: loading,
                pageNumber: pageNumber,
                onSortChange: handleSortChange,
                onFilterChange: handleFilterChange,
                onResetFilters: ()=>handleFilterChange(defaultFilters),
                onPageNumberChange: handlePageNumberChange,
                retry: async function retry() {
                    let { disableFetchInvoices } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
                    await Promise.all(compact([
                        !disableFetchInvoices && refresh(),
                        onRetry && onRetry()
                    ]));
                }
            });
            return renderChild ? renderChild({
                data,
                loading,
                tableContent,
                exportingCsv,
                refresh,
                getRows: ()=>data.invoices,
                async exportCsv () {
                    setExportingCsv(true);
                    const params = mapSortAndFiltersRequestParams(_object_spread_props(_object_spread({}, urlStateRef.current), {
                        sort: sortRef.current,
                        sortIds,
                        now: getNow()
                    }));
                    const result = await handlePromise(api.invoice.exportToCsv(params));
                    if (result.success) {
                        const filename = exportCsvFilename || 'exported-invoices.csv';
                        downloadCsv(result.data, filename);
                    } else {
                        showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'export-invoices-error');
                    }
                    setExportingCsv(false);
                }
            }) : tableContent;
        }
    });
}
